import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ParallaxImage from "@/components/ParallaxImage";
import EditorialMarquee from "@/components/EditorialMarquee";

const experiences = [
  {
    num: "01",
    title: "Outdoor Adventure Days",
    tagline: "Step into a day of adventure",
    features: ["Out and about in nature", "Breathing and cold water activities", "Bespoke activities available", "A Master Coach on hand", "Learn and understand yourself"],
    benefits: ["Unplug and de-stress", "Connect with people and nature", "Spend the day recharging", "Create magical memories"],
    img: "/images/rx4/breathwork-lakeside.webp",
    alt: "RX4 breathwork session on the lakeshore",
  },
  {
    num: "02",
    title: "1-to-1 Coaching Walks",
    tagline: "A leadership growth opportunity",
    features: ["1-to-1 with a Master Coach", "Tailored to the individual", "A day of walking, talking and understanding in nature", "Learn techniques to change your life"],
    benefits: ["Heightened sense of self", "Understand what it means to be fully present", "Learn how to create freedom", "Methods to support personal growth"],
    img: "/images/rx4/dave-rocks.webp",
    alt: "Dave sat by the lake with his RX4 pack",
  },
  {
    num: "03",
    title: "Keynotes & Workshops",
    tagline: "For teams of any size",
    features: ["Keynote and workshop style presentations", "Tailored to your team's physical and mental wellbeing", "Supporting individual and business culture", "Suited to any group size"],
    benefits: ["Feel less stressed and more in control", "Improved daily structure", "A more positive attitude and outlook", "Goal setting, and goal getting"],
    img: "/images/rx4/workshop-keynote.webp",
    alt: "Dave delivering an RX4 workshop",
  },
  {
    num: "04",
    title: "Corporate Wellbeing",
    tagline: "Get the best out of your people",
    features: ["Motivational and behavioural talks", "Outdoor experiences for staff wellbeing", "Physical and mental wellbeing focus", "Packages tailored to your company"],
    benefits: ["A better culture in the business", "Support and get the best out of staff", "Serve your customers at the highest level", "Business-only or community programmes"],
    img: "/images/rx4/group-hike.jpg",
    alt: "RX4 group walking a misty valley path",
  },
  {
    num: "05",
    title: "The RX4 Wild Sauna",
    tagline: "Heat, then plunge",
    features: ["Wood-fired wild sauna by the sea", "Contrast ritual with cold water plunge", "Joins selected events and community sessions", "Available for private bookings"],
    benefits: ["Deep physical recovery", "A shared ritual that bonds teams", "The RX4 reset in its purest form", "Unforgettable by the water"],
    img: "/images/rx4/sauna-trailer.jpg",
    alt: "The RX4 Wild Sauna trailer by the sea",
  },
];

const Experiences = () => (
  <main data-testid="experiences-page">
    <PageHero
      testId="experiences-hero"
      overline="RX4 Experiences"
      lines={["Choose your", "arena."]}
      sub="Personal and professional programmes, outdoor adventures, 1-to-1 coaching, keynotes and the wild sauna. Bespoke packages always available."
      image="/images/rx4/sauna-trailer.jpg"
      alt="The RX4 Wild Sauna trailer with the community by the sea"
    />

    <section className="section-shell space-y-24 md:space-y-36" data-testid="experiences-list">
      {experiences.map((p, i) => (
        <div key={p.num} data-testid={`experience-${p.num}`} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
            <div className="relative">
              <span className="absolute -top-4 left-4 font-mono text-sm text-brand bg-ink border border-white/10 rounded-full px-4 py-1.5 z-10">[ {p.num} ]</span>
              <ParallaxImage src={p.img} alt={p.alt} className="aspect-[4/5] rounded-2xl" speed={0.1} />
            </div>
          </Reveal>
          <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
            <Reveal>
              <p className="overline-label mb-3">{p.tagline}</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-bone mb-8">{p.title}</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Reveal delay={0.1}>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">The experience</p>
                <ul className="space-y-3">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-slate-300 text-sm" data-testid={`experience-${p.num}-feature-${j}`}>
                      <Check size={16} className="text-brand mt-0.5 shrink-0" strokeWidth={2.5} /> {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Benefits</p>
                <ul className="space-y-3">
                  {p.benefits.map((b, j) => (
                    <li key={j} className="font-display font-medium text-bone" data-testid={`experience-${p.num}-benefit-${j}`}>{b}</li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <Reveal delay={0.25}>
              <Link to="/contact" data-testid={`experience-${p.num}-enquire-btn`} className="btn-primary group mt-8">
                Enquire Now
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      ))}
    </section>

    <EditorialMarquee items={["BESPOKE PACKAGES", "AVAILABLE ON REQUEST"]} />

    {/* NOT SURE */}
    <section className="bg-ink-soft border-t border-white/5" data-testid="not-sure-section">
      <div className="section-shell text-center">
        <Reveal>
          <p className="overline-label mb-4">Not sure what you need?</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-bone max-w-2xl mx-auto leading-[1.1]">
            Don't worry. We are here to help.
          </h2>
          <p className="mt-6 text-slate-400 max-w-xl mx-auto">
            Let's have a chat about what you are looking for and where you need support. We offer business-only and community involvement programmes.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/apply" data-testid="not-sure-apply-btn" className="btn-primary group">
              Take the Eligibility Check <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a href="mailto:rx4lakes@outlook.com" data-testid="not-sure-email-btn" className="btn-secondary">
              rx4lakes@outlook.com
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Experiences;
